/**
 * Request Card Component
 * Displays a token request with status and actions
 */
import React from 'react';
import PropTypes from 'prop-types';
import { formatDate } from '../utils/dateHelpers';
import logger from '../utils/logger';

const RequestCard = ({
  request,
  employee,
  onApprove,
  onReject,
  onView,
  showActions = false,
  onCancel,
  showCancelButton = false,
  onApproveCancellation,
  onRejectCancellation,
  employees = [],
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'cancellation_requested':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  logger.debug('RequestCard rendered', { requestId: request.id, status: request.status });
  
  const getStatusGradient = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-gradient-to-r from-green-500 to-emerald-500';
      case 'rejected':
        return 'bg-gradient-to-r from-red-500 to-rose-500';
      case 'pending':
        return 'bg-gradient-to-r from-yellow-400 to-amber-500';
      case 'cancellation_requested':
        return 'bg-gradient-to-r from-orange-500 to-amber-500';
      case 'cancelled':
        return 'bg-gradient-to-r from-gray-400 to-gray-500';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-500';
    }
  };
  
  const getStatusLabel = (status) => {
    switch (status) {
      case 'cancellation_requested':
        return 'CANCELLATION REQUESTED';
      case 'cancelled':
        return 'CANCELLED';
      default:
        return status.toUpperCase();
    }
  };

  return (
    <div className="glass p-6 hover:shadow-lg hover:border-white/30 transition-all duration-200 group">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center text-white font-bold shadow-md">
            {employee?.name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              {employee?.name || 'Unknown Employee'}
            </h3>
            <p className="text-sm text-gray-500 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
              {employee?.email || ''}
            </p>
          </div>
        </div>
        <span
          className={`px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-md ${getStatusGradient(
            request.status
          )}`}
        >
          {getStatusLabel(request.status)}
        </span>
      </div>
      
      <div className="space-y-3 mb-4 pl-1">
        <div className="flex items-center text-sm text-gray-700">
          <svg className="w-5 h-5 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="font-semibold mr-2">Dates:</span>
          <span>
            {formatDate(request.startDate)} - {formatDate(request.endDate)}
          </span>
        </div>
        <div className="flex items-center text-sm text-gray-700">
          <svg className="w-5 h-5 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-semibold mr-2">Duration:</span>
          <span className="px-2 py-1 bg-primary-50 text-primary-700 rounded-lg font-semibold">
            {request.days || 1} {request.days === 1 ? 'day' : 'days'}
          </span>
        </div>
        {request.reason && (
          <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-100">
            <span className="font-semibold text-gray-600 block mb-1">Reason:</span>
            <span className="text-gray-700">{request.reason}</span>
          </div>
        )}
        {request.cancellationReason && (
          <div className="text-sm text-gray-700 bg-orange-50 rounded-lg p-3 border border-orange-100">
            <span className="font-semibold text-orange-600 block mb-1">Cancellation Reason:</span>
            <span className="text-gray-700">{request.cancellationReason}</span>
          </div>
        )}
        {request.reviewedAt && (
          <div className="text-xs text-gray-500 flex items-center pt-2 border-t border-gray-100">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Reviewed on {formatDate(request.reviewedAt)} by {employees.find(emp => emp.id === request.reviewedBy)?.name || request.reviewedBy || 'Manager'}
          </div>
        )}
      </div>
      
      <div className="flex gap-3 pt-4 border-t border-gray-100">
        {onView && (
          <button
            onClick={() => onView(request)}
            className="px-4 py-2 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>View Details</span>
          </button>
        )}
        {showCancelButton && ['pending', 'approved'].includes(request.status) && onCancel && (
          <button
            onClick={() => onCancel(request)}
            className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Request Cancellation</span>
          </button>
        )}
        {showActions && request.status === 'pending' && (
          <>
            {onApprove && (
              <button
                onClick={() => onApprove(request)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Approve</span>
              </button>
            )}
            {onReject && (
              <button
                onClick={() => onReject(request)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Reject</span>
              </button>
            )}
          </>
        )}
        {showActions && request.status === 'cancellation_requested' && (
          <>
            {onApproveCancellation && (
              <button
                onClick={() => onApproveCancellation(request)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Approve Cancellation</span>
              </button>
            )}
            {onRejectCancellation && (
              <button
                onClick={() => onRejectCancellation(request)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-yellow-500 to-amber-600 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Reject Cancellation</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

RequestCard.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.string.isRequired,
    employeeId: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    days: PropTypes.number,
    status: PropTypes.oneOf(['pending', 'approved', 'rejected', 'cancellation_requested', 'cancelled']).isRequired,
    reason: PropTypes.string,
    cancellationReason: PropTypes.string,
    reviewedAt: PropTypes.string,
    reviewedBy: PropTypes.string,
  }).isRequired,
  employee: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  employees: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
  })),
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onView: PropTypes.func,
  showActions: PropTypes.bool,
  onCancel: PropTypes.func,
  showCancelButton: PropTypes.bool,
  onApproveCancellation: PropTypes.func,
  onRejectCancellation: PropTypes.func,
};

export default RequestCard;

